import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Cloud, Loader2 } from "lucide-react";
import type {
  JourneyTaskAction,
  JourneyTaskActionKind,
  JourneyTaskRow,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { useForms } from "../forms/use-forms";
import { useContentAssets } from "../content/use-content-assets";
import { useUpdateTask } from "./use-update-task";

// Phase 7 sub-bucket 14 — per-task configuration sheet.
//
// Right-side Sheet hosting the full task surface. Base fields (description,
// due offset, visibility, assignee_kind) + discriminated-union form on
// action.kind. Eight kinds in two groups:
//
//   "Partner action"          | "Admin shares content" (new sub-bucket 14)
//   - none                    | - info_note     (admin markdown body)
//   - form_submission         | - info_link     (admin href + label)
//   - note (partner-supplied) | - info_attachment (admin asset_id)
//   - link (partner-supplied) |
//   - attachment (partner-...)|
//
// Per-field autosave (useAutosave → useUpdateTask, debounce 600ms). On kind
// swap, we emit a new full `action` object via flushPatch immediately — no
// debounce — because the discriminated union shape must transition atomically
// (e.g. "info_link" → "form_submission" would otherwise momentarily carry
// orphan `href`+`label` fields).
//
// Asset picker on info_attachment is inline; once a third consumer appears
// (likely admin MDF receipts in sub-bucket 15) extract to
// components/shared/asset-picker.tsx.
// TODO(sub-bucket 15): extract asset picker when admin MDF needs it too.

const KIND_LABEL: Record<JourneyTaskActionKind, string> = {
  none: "No action — manual completion",
  form_submission: "Form submission",
  note: "Note required (partner)",
  link: "Link required (partner)",
  attachment: "Attachment required (partner)",
  info_note: "Shared note (admin authors)",
  info_link: "Shared link (admin authors)",
  info_attachment: "Shared file (admin authors)",
};

export function TaskConfigSheet({
  journeyId,
  taskId,
  tasks,
  onClose,
}: {
  journeyId: string;
  taskId: string;
  tasks: JourneyTaskRow[];
  onClose: () => void;
}) {
  const task = useMemo(
    () => tasks.find((t) => t.id === taskId),
    [tasks, taskId],
  );
  const updateTask = useUpdateTask();

  // The mutation's optimistic Pattern B already patches the journey-detail
  // cache, so caller renders see the latest values without per-field state
  // duplication here at the page level. We just hand the patcher down.
  const flushPatch = useCallback(
    async (patch: Parameters<typeof updateTask.mutateAsync>[0]["patch"]) => {
      await updateTask.mutateAsync({ journeyId, taskId, patch });
    },
    [journeyId, taskId, updateTask],
  );

  const status: SaveStatus = updateTask.isPending
    ? "saving"
    : updateTask.isError
      ? "error"
      : "idle";

  if (!task) return null;

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md"
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="font-display tracking-tight">
            Task settings
          </SheetTitle>
          <SheetDescription>Changes save automatically.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5">
          <BaseFields task={task} flushPatch={flushPatch} />
          <ActionConfig task={task} flushPatch={flushPatch} />
        </div>

        <SheetFooter className="mt-6 flex items-center justify-between gap-2 border-t pt-4">
          <SaveIndicator state={status} />
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

type FlushPatch = (
  patch: Parameters<ReturnType<typeof useUpdateTask>["mutateAsync"]>[0]["patch"],
) => Promise<unknown>;

function BaseFields({
  task,
  flushPatch,
}: {
  task: JourneyTaskRow;
  flushPatch: FlushPatch;
}) {
  const [description, setDescription] = useState(task.description ?? "");
  const [dueOffsetDays, setDueOffsetDays] = useState(String(task.due_offset_days));

  useEffect(() => {
    setDescription(task.description ?? "");
  }, [task.description]);
  useEffect(() => {
    setDueOffsetDays(String(task.due_offset_days));
  }, [task.due_offset_days]);

  const descAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      await flushPatch({ description: trimmed ? trimmed : null });
    },
    wait: 600,
  });
  const dueAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const parsed = Number.parseInt(next, 10);
      if (Number.isNaN(parsed) || parsed < 0) return;
      await flushPatch({ due_offset_days: parsed });
    },
    wait: 600,
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="ts-description">Description</Label>
        <Textarea
          id="ts-description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            descAutosave.queue(e.target.value);
          }}
          placeholder="Optional context shown above the task in the portal."
          rows={3}
          maxLength={2000}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ts-due">Due offset (days)</Label>
          <Input
            id="ts-due"
            type="number"
            min={0}
            max={400}
            value={dueOffsetDays}
            onChange={(e) => {
              setDueOffsetDays(e.target.value);
              dueAutosave.queue(e.target.value);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ts-visibility">Visibility</Label>
          <Select
            value={task.visibility}
            onValueChange={(v) =>
              void flushPatch({ visibility: v as JourneyTaskRow["visibility"] })
            }
          >
            <SelectTrigger id="ts-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public (partner sees)</SelectItem>
              <SelectItem value="internal">Internal only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ts-assignee">Assignee</Label>
        <Select
          value={task.assignee_kind}
          onValueChange={(v) =>
            void flushPatch({ assignee_kind: v as JourneyTaskRow["assignee_kind"] })
          }
        >
          <SelectTrigger id="ts-assignee">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="vendor">Bookboost (internal)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ActionConfig({
  task,
  flushPatch,
}: {
  task: JourneyTaskRow;
  flushPatch: FlushPatch;
}) {
  const kind = task.action.kind;

  function defaultsForKind(next: JourneyTaskActionKind): JourneyTaskAction {
    switch (next) {
      case "none":
        return { kind: "none" };
      case "form_submission":
        return { kind: "form_submission", form_id: "", complete_on: "accepted" };
      case "note":
        return { kind: "note", prompt: null };
      case "link":
        return { kind: "link", prompt: null };
      case "attachment":
        return { kind: "attachment", prompt: null, multiple: false };
      case "info_note":
        return { kind: "info_note", body: "" };
      case "info_link":
        return { kind: "info_link", href: "", label: null };
      case "info_attachment":
        return { kind: "info_attachment", asset_id: "" };
    }
  }

  function handleKindChange(next: JourneyTaskActionKind) {
    if (next === kind) return;
    void flushPatch({ action: defaultsForKind(next) });
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-3.5">
      <div className="space-y-1.5">
        <Label htmlFor="ts-action-kind">Action</Label>
        <Select
          value={kind}
          onValueChange={(v) => handleKindChange(v as JourneyTaskActionKind)}
        >
          <SelectTrigger id="ts-action-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Partner action</SelectLabel>
              <SelectItem value="none">{KIND_LABEL.none}</SelectItem>
              <SelectItem value="form_submission">{KIND_LABEL.form_submission}</SelectItem>
              <SelectItem value="note">{KIND_LABEL.note}</SelectItem>
              <SelectItem value="link">{KIND_LABEL.link}</SelectItem>
              <SelectItem value="attachment">{KIND_LABEL.attachment}</SelectItem>
            </SelectGroup>
            <SelectGroup>
              <SelectLabel>Admin shares content</SelectLabel>
              <SelectItem value="info_note">{KIND_LABEL.info_note}</SelectItem>
              <SelectItem value="info_link">{KIND_LABEL.info_link}</SelectItem>
              <SelectItem value="info_attachment">{KIND_LABEL.info_attachment}</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <ActionFields action={task.action} flushPatch={flushPatch} />
    </div>
  );
}

function ActionFields({
  action,
  flushPatch,
}: {
  action: JourneyTaskAction;
  flushPatch: FlushPatch;
}) {
  switch (action.kind) {
    case "none":
      return (
        <p className="text-xs text-muted-foreground">
          The partner ticks a checkbox to mark this task complete. No proof
          payload or admin content.
        </p>
      );
    case "form_submission":
      return <FormSubmissionFields action={action} flushPatch={flushPatch} />;
    case "note":
    case "link":
    case "attachment":
      return <PartnerProofFields action={action} flushPatch={flushPatch} />;
    case "info_note":
      return <InfoNoteFields action={action} flushPatch={flushPatch} />;
    case "info_link":
      return <InfoLinkFields action={action} flushPatch={flushPatch} />;
    case "info_attachment":
      return <InfoAttachmentFields action={action} flushPatch={flushPatch} />;
  }
}

function FormSubmissionFields({
  action,
  flushPatch,
}: {
  action: Extract<JourneyTaskAction, { kind: "form_submission" }>;
  flushPatch: FlushPatch;
}) {
  const forms = useForms();
  const formList = forms.data ?? [];
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="ts-form-id">Form</Label>
        <Select
          value={action.form_id || "__unset__"}
          onValueChange={(v) =>
            void flushPatch({
              action: { ...action, form_id: v === "__unset__" ? "" : v },
            })
          }
        >
          <SelectTrigger id="ts-form-id">
            <SelectValue placeholder="Pick a form…" />
          </SelectTrigger>
          <SelectContent>
            {formList.length === 0 && (
              <SelectItem value="__unset__" disabled>
                No forms available
              </SelectItem>
            )}
            {formList.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ts-complete-on">Complete when</Label>
        <Select
          value={action.complete_on}
          onValueChange={(v) =>
            void flushPatch({
              action: { ...action, complete_on: v as "submitted" | "accepted" },
            })
          }
        >
          <SelectTrigger id="ts-complete-on">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="submitted">Partner submits</SelectItem>
            <SelectItem value="accepted">Internal user accepts</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function PartnerProofFields({
  action,
  flushPatch,
}: {
  action: Extract<JourneyTaskAction, { kind: "note" | "link" | "attachment" }>;
  flushPatch: FlushPatch;
}) {
  const [prompt, setPrompt] = useState(action.prompt ?? "");
  useEffect(() => {
    setPrompt(action.prompt ?? "");
  }, [action.prompt]);
  const promptAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      await flushPatch({
        action: { ...action, prompt: trimmed ? trimmed : null },
      });
    },
    wait: 600,
  });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="ts-prompt">Hint copy (optional)</Label>
        <Textarea
          id="ts-prompt"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            promptAutosave.queue(e.target.value);
          }}
          placeholder='e.g. "Paste the campaign brief link"'
          rows={2}
          maxLength={500}
        />
      </div>
      {action.kind === "attachment" && (
        <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2">
          <div>
            <Label className="text-sm font-medium">Multiple files</Label>
            <p className="text-xs text-muted-foreground">
              Partner can upload up to 10 attachments.
            </p>
          </div>
          <input
            type="checkbox"
            checked={action.multiple}
            onChange={(e) =>
              void flushPatch({
                action: { ...action, multiple: e.target.checked },
              })
            }
            className="h-4 w-4 cursor-pointer accent-foreground"
          />
        </div>
      )}
    </div>
  );
}

function InfoNoteFields({
  action,
  flushPatch,
}: {
  action: Extract<JourneyTaskAction, { kind: "info_note" }>;
  flushPatch: FlushPatch;
}) {
  const [body, setBody] = useState(action.body);
  useEffect(() => {
    setBody(action.body);
  }, [action.body]);
  const autosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      if (!trimmed) return;
      await flushPatch({ action: { kind: "info_note", body: trimmed } });
    },
    wait: 600,
  });
  return (
    <div className="space-y-1.5">
      <Label htmlFor="ts-info-note">Body</Label>
      <Textarea
        id="ts-info-note"
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          autosave.queue(e.target.value);
        }}
        placeholder="What should the partner read? Plain text or simple markdown."
        rows={5}
        maxLength={4000}
        required
      />
      <p className="text-xs text-muted-foreground">
        Up to 4,000 characters. Partner ticks the checkbox to acknowledge.
      </p>
    </div>
  );
}

function InfoLinkFields({
  action,
  flushPatch,
}: {
  action: Extract<JourneyTaskAction, { kind: "info_link" }>;
  flushPatch: FlushPatch;
}) {
  const [href, setHref] = useState(action.href);
  const [label, setLabel] = useState(action.label ?? "");
  useEffect(() => {
    setHref(action.href);
  }, [action.href]);
  useEffect(() => {
    setLabel(action.label ?? "");
  }, [action.label]);
  const hrefAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      if (!trimmed) return;
      await flushPatch({
        action: {
          kind: "info_link",
          href: trimmed,
          label: label.trim() ? label.trim() : null,
        },
      });
    },
    wait: 600,
  });
  const labelAutosave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      await flushPatch({
        action: { kind: "info_link", href, label: trimmed ? trimmed : null },
      });
    },
    wait: 600,
  });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="ts-info-href">URL</Label>
        <Input
          id="ts-info-href"
          type="url"
          value={href}
          onChange={(e) => {
            setHref(e.target.value);
            hrefAutosave.queue(e.target.value);
          }}
          placeholder="https://…"
          required
          maxLength={2048}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ts-info-label">Label (optional)</Label>
        <Input
          id="ts-info-label"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            labelAutosave.queue(e.target.value);
          }}
          placeholder="Falls back to the URL if empty"
          maxLength={200}
        />
      </div>
    </div>
  );
}

function InfoAttachmentFields({
  action,
  flushPatch,
}: {
  action: Extract<JourneyTaskAction, { kind: "info_attachment" }>;
  flushPatch: FlushPatch;
}) {
  // Inline asset picker — first consumer. If admin MDF needs the same in
  // sub-bucket 15, extract to components/shared/asset-picker.tsx.
  const assets = useContentAssets({ archived: false });
  const assetList = (assets.data ?? []).filter((a) => !a.is_folder);
  return (
    <div className="space-y-1.5">
      <Label htmlFor="ts-info-asset">Content asset</Label>
      <Select
        value={action.asset_id || "__unset__"}
        onValueChange={(v) =>
          void flushPatch({
            action: {
              kind: "info_attachment",
              asset_id: v === "__unset__" ? "" : v,
            },
          })
        }
      >
        <SelectTrigger id="ts-info-asset">
          <SelectValue placeholder="Pick a file…" />
        </SelectTrigger>
        <SelectContent>
          {assetList.length === 0 && (
            <SelectItem value="__unset__" disabled>
              No assets available
            </SelectItem>
          )}
          {assetList.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {a.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Partner sees a Download button on this task and ticks the checkbox to
        acknowledge.
      </p>
    </div>
  );
}

function SaveIndicator({ state }: { state: SaveStatus }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-status-active">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        Save failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
      <Cloud className="h-3 w-3" />
      Auto-save on
    </span>
  );
}
